import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Settings as SettingsIcon, Lock, Eye, EyeOff } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

export default function Settings() {
  const { isAdmin } = useAuth();
  const [packSizes, setPackSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  const [formData, setFormData] = useState({
    size_kg: '',
    units_per_bale: '',
    packaging_papers_per_bale: '',
    cost_per_paper: '',
    outer_wrap_cost: ''
  });
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchPackSizes();
  }, []);

  const fetchPackSizes = async () => {
    try {
      const response = await api.get('/pack-sizes');
      setPackSizes(response.data);
    } catch (error) {
      toast.error('Failed to load pack sizes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.size_kg || !formData.units_per_bale) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      const payload = {
        size_kg: parseFloat(formData.size_kg),
        units_per_bale: parseInt(formData.units_per_bale),
        packaging_papers_per_bale: parseInt(formData.packaging_papers_per_bale) || 0,
        cost_per_paper: parseFloat(formData.cost_per_paper) || 0,
        outer_wrap_cost: parseFloat(formData.outer_wrap_cost) || 0
      };

      if (editingPack) {
        await api.put(`/pack-sizes/${editingPack.id}`, payload);
        toast.success('Pack size updated');
      } else {
        await api.post('/pack-sizes', payload);
        toast.success('Pack size created');
      }
      setDialogOpen(false);
      resetForm();
      fetchPackSizes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pack size');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pack size?')) return;
    try {
      await api.delete(`/pack-sizes/${id}`);
      toast.success('Pack size deleted');
      fetchPackSizes();
    } catch (error) {
      toast.error('Failed to delete pack size');
    }
  };

  const resetForm = () => {
    setFormData({
      size_kg: '',
      units_per_bale: '',
      packaging_papers_per_bale: '',
      cost_per_paper: '',
      outer_wrap_cost: ''
    });
    setEditingPack(null);
  };

  const openEditDialog = (pack) => {
    setEditingPack(pack);
    setFormData({
      size_kg: pack.size_kg.toString(),
      units_per_bale: pack.units_per_bale.toString(),
      packaging_papers_per_bale: pack.packaging_papers_per_bale.toString(),
      cost_per_paper: pack.cost_per_paper.toString(),
      outer_wrap_cost: pack.outer_wrap_cost.toString()
    });
    setDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Please fill all password fields');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', passwordData);
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Settings</h1>
          <p className="text-slate-500 mt-1">System configuration</p>
        </div>
      </div>

      {/* Packaging Rules */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <SettingsIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-heading">Packaging Rules</CardTitle>
              <p className="text-sm text-slate-500">Configure pack sizes and packaging costs</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2" data-testid="add-pack-size-btn">
                <Plus className="h-4 w-4" />
                Add Pack Size
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingPack ? 'Edit Pack Size' : 'Add Pack Size'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Size (kg) *</Label>
                    <Input
                      type="number"
                      value={formData.size_kg}
                      onChange={(e) => setFormData({ ...formData, size_kg: e.target.value })}
                      placeholder="e.g., 1"
                      step="0.1"
                      data-testid="pack-size-kg-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Units per Bale *</Label>
                    <Input
                      type="number"
                      value={formData.units_per_bale}
                      onChange={(e) => setFormData({ ...formData, units_per_bale: e.target.value })}
                      placeholder="e.g., 12"
                      data-testid="pack-units-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Papers per Bale</Label>
                    <Input
                      type="number"
                      value={formData.packaging_papers_per_bale}
                      onChange={(e) => setFormData({ ...formData, packaging_papers_per_bale: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cost per Paper (KES)</Label>
                    <Input
                      type="number"
                      value={formData.cost_per_paper}
                      onChange={(e) => setFormData({ ...formData, cost_per_paper: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Outer Wrap Cost (KES)</Label>
                  <Input
                    type="number"
                    value={formData.outer_wrap_cost}
                    onChange={(e) => setFormData({ ...formData, outer_wrap_cost: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="pack-size-submit-btn">
                    {editingPack ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Size (kg)</TableHead>
                <TableHead className="text-right">Units/Bale</TableHead>
                <TableHead className="text-right">Papers/Bale</TableHead>
                <TableHead className="text-right">Cost/Paper</TableHead>
                <TableHead className="text-right">Outer Wrap</TableHead>
                <TableHead className="text-right">Total Cost/Bale</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packSizes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No pack sizes configured
                  </TableCell>
                </TableRow>
              ) : (
                packSizes.map((pack) => (
                  <TableRow key={pack.id} className="table-row" data-testid={`pack-row-${pack.id}`}>
                    <TableCell className="font-medium">{pack.size_kg}kg</TableCell>
                    <TableCell className="text-right">{pack.units_per_bale}</TableCell>
                    <TableCell className="text-right">{pack.packaging_papers_per_bale}</TableCell>
                    <TableCell className="text-right">{formatCurrency(pack.cost_per_paper)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(pack.outer_wrap_cost)}</TableCell>
                    <TableCell className="text-right font-medium text-[#1B4D3E]">
                      {formatCurrency(pack.packaging_cost_per_bale)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(pack)} data-testid={`edit-pack-${pack.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(pack.id)} data-testid={`delete-pack-${pack.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Company Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Company Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-500">Company Name</p>
              <p className="font-medium">Primefield Mills & Commodities Limited</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Primary Brand</p>
              <p className="font-medium">MIALE</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Address</p>
              <p className="font-medium">P.O BOX 593 - 00219</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Phone</p>
              <p className="font-medium">0118707187</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Email</p>
              <p className="font-medium">Primefieldmills@gmail.com</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Tagline</p>
              <p className="font-medium italic">Pure Goodness in Every Meal</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Admin Password - Admin Only */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Lock className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-heading">Change Admin Password</CardTitle>
                <p className="text-sm text-slate-500">Update your administrator password</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label>Current Password *</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>New Password *</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Enter new password (min 6 characters)"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirm New Password *</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={changingPassword}
                data-testid="change-password-btn"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
      {isAdmin && <PackagingConsumptionRates />}
    </div>
  );
}

// ================= Phase 3: Packaging Consumption Rates =================
function PackagingConsumptionRates() {
  const [rates, setRates] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ pack_size: '2kg', commodity_id: '', basis: 'per_bale', quantity: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [rr, ii] = await Promise.all([api.get('/packaging-consumption-rates'), api.get('/commodity-items')]);
      setRates(rr.data || []); setItems(ii.data || []);
    } catch { toast.error('Failed to load consumption rates'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.commodity_id) return toast.error('Select commodity');
    try {
      await api.post('/packaging-consumption-rates', { pack_size: form.pack_size, commodity_id: form.commodity_id, basis: form.basis, quantity: parseFloat(form.quantity) });
      toast.success('Rate saved');
      setForm({ pack_size: form.pack_size, commodity_id: '', basis: 'per_bale', quantity: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this rate?')) return;
    try { await api.delete(`/packaging-consumption-rates/${id}`); load(); } catch { toast.error('Delete failed'); }
  };

  const packSizes = ['0.5kg', '1kg', '2kg', '50kg'];
  return (
    <Card data-testid="packaging-rates-card">
      <CardHeader>
        <CardTitle className="font-heading">Packaging / Commodity Consumption Rates</CardTitle>
        <p className="text-sm text-slate-500">Configure per-pack-size consumption rules. Production will auto-consume commodity stock based on these rates.</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4">
          <div className="space-y-1">
            <Label className="text-xs">Pack Size</Label>
            <Select value={form.pack_size} onValueChange={(v) => setForm({ ...form, pack_size: v })}>
              <SelectTrigger data-testid="rate-packsize"><SelectValue /></SelectTrigger>
              <SelectContent>{packSizes.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Commodity</Label>
            <Select value={form.commodity_id} onValueChange={(v) => setForm({ ...form, commodity_id: v })}>
              <SelectTrigger data-testid="rate-commodity"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Basis</Label>
            <Select value={form.basis} onValueChange={(v) => setForm({ ...form, basis: v })}>
              <SelectTrigger data-testid="rate-basis"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="per_bale">Per Bale</SelectItem><SelectItem value="per_packet">Per Packet</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Quantity</Label>
            <Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required data-testid="rate-quantity" />
          </div>
          <div className="flex items-end"><Button type="submit" className="btn-primary w-full" data-testid="rate-save-btn"><Plus className="h-4 w-4 mr-1" />Save Rate</Button></div>
        </form>
        <Table>
          <TableHeader><TableRow className="table-header">
            <TableHead>Pack Size</TableHead><TableHead>Commodity</TableHead><TableHead>Basis</TableHead>
            <TableHead className="text-right">Quantity</TableHead><TableHead className="text-center">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-4">Loading…</TableCell></TableRow> :
             rates.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">No consumption rates configured</TableCell></TableRow> :
             rates.map(r => (
               <TableRow key={r.id} data-testid={`rate-row-${r.id}`}>
                 <TableCell><Badge variant="outline">{r.pack_size}</Badge></TableCell>
                 <TableCell className="font-medium">{r.commodity_name}</TableCell>
                 <TableCell className="capitalize">{(r.basis || '').replace('_', ' ')}</TableCell>
                 <TableCell className="text-right">{r.quantity}</TableCell>
                 <TableCell className="text-center"><Button variant="ghost" size="sm" onClick={() => remove(r.id)} data-testid={`rate-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
               </TableRow>
             ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
